<script setup lang="ts">
import { ref, watch } from 'vue'

const files = defineModel<File[]>({ default: () => [] })

const errorMessage = ref<string | null>(null)

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

function isAcceptedType(file: File): boolean {
  if (file.type) {
    if (file.type.startsWith('image/') || file.type === 'application/pdf' || file.type === 'text/plain') {
      return true
    }
  }
  const ext = file.name.split('.').pop()?.toLowerCase()
  return ext === 'txt' || ext === 'log' || ext === 'pdf' || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext || '')
}

let isInternalUpdate = false

watch(
  files,
  (newFiles) => {
    if (isInternalUpdate) {
      isInternalUpdate = false
      return
    }

    if (!newFiles || newFiles.length === 0) {
      errorMessage.value = null
      return
    }

    const validFiles: File[] = []
    let error: string | null = null

    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        error = `Die Datei "${file.name}" überschreitet das Limit von 10 MB.`
      } else if (!isAcceptedType(file)) {
        error = `Die Datei "${file.name}" hat einen nicht unterstützten Dateityp. Erlaubt sind Bilder, PDF und Textdateien.`
      } else {
        validFiles.push(file)
      }
    }

    if (error) {
      errorMessage.value = error
      if (validFiles.length !== newFiles.length) {
        isInternalUpdate = true
        files.value = validFiles
      }
    } else {
      errorMessage.value = null
    }
  },
  { deep: true }
)
</script>

<template>
  <div class="w-full space-y-2">
    <UFormField label="Anhänge (Bilder, Logs)" name="files" class="w-full">
      <UFileUpload
        v-model="files"
        multiple
        label="Dateien hierher ziehen oder klicken"
        description="Bilder oder Dokumente bis 10MB"
        accept="image/*,application/pdf,text/plain"
        layout="list"
        class="w-full"
      />
    </UFormField>

    <UAlert
      v-if="errorMessage"
      color="error"
      variant="subtle"
      icon="i-lucide-alert-circle"
      :title="errorMessage"
      class="mt-2"
    />
  </div>
</template>
